import type { ReactNode } from 'react';
import type { MetricDto } from '@crm/shared/contracts';
import { DeltaBadge } from '../../components/DeltaBadge.tsx';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { Skeleton } from '../../components/ui/skeleton.tsx';
import { DASH } from '../../lib/format.ts';
import { useT } from '../../lib/i18n.tsx';

export interface StatTileProps {
  label: string;
  definition: ReactNode;
  /** Formatted value; `undefined` while loading. */
  value: string | undefined;
  unit?: string;
  /** Comparison with the previous period. */
  metric?: MetricDto | undefined;
  showDelta?: boolean;
  /** Short context under the value. */
  note?: ReactNode;
  /** Set when the source cannot supply the figure: said in words, never shown as 0. */
  unavailable?: string | undefined;
  /** Formats amounts in the comparison (e.g. money with its currency). */
  formatAmount?: (value: number) => string;
}

/**
 * One figure in a section's metric strip: label, a large value, the change against the comparison
 * period and a definition behind the info button. Tiles sit side by side separated by hairlines,
 * so a strip reads as one row of numbers rather than a wall of cards.
 */
export function StatTile({
  label,
  definition,
  value,
  unit,
  metric,
  showDelta = true,
  note,
  unavailable,
  formatAmount,
}: StatTileProps) {
  const t = useT();
  const loading = value === undefined && !unavailable;
  return (
    <div className="flex min-w-0 flex-col gap-1 p-4" aria-busy={loading}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-muted-foreground">{label}</h3>
        <InfoPopover label={label}>
          <p>{definition}</p>
        </InfoPopover>
      </div>
      <div className="min-h-9">
        {unavailable ? (
          <p className="pt-1 text-base text-muted-foreground">{t.common.notAvailable}</p>
        ) : loading ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <p className="inline-flex items-baseline gap-1.5">
            <span
              className="text-[1.75rem] leading-9 font-semibold tracking-tight"
              {...(value === DASH ? { 'aria-label': t.common.noData } : {})}
            >
              {value}
            </span>
            {unit && value !== DASH ? (
              <span className="text-sm text-muted-foreground">{unit}</span>
            ) : null}
          </p>
        )}
      </div>
      {unavailable ? <p className="text-xs text-muted-foreground">{unavailable}</p> : null}
      {!unavailable && showDelta && metric ? (
        <DeltaBadge metric={metric} {...(formatAmount ? { formatAmount } : {})} />
      ) : null}
      {!unavailable && note ? <div className="text-xs text-muted-foreground">{note}</div> : null}
    </div>
  );
}

/** A row of tiles in one card, divided by hairlines; wraps to two columns on phones. */
export function StatStrip({ children, columns }: { children: ReactNode; columns: number }) {
  const cols: Record<number, string> = {
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
  };
  return (
    <div
      className={`grid grid-cols-2 divide-border rounded-lg border bg-card shadow-xs max-lg:[&>*:nth-child(2n)]:border-l max-lg:[&>*:nth-child(n+3)]:border-t lg:divide-x ${cols[columns] ?? 'lg:grid-cols-4'}`}
    >
      {children}
    </div>
  );
}

/** A titled part of the home page: heading, one-line explanation, then its content. */
export function HomeSection({
  id,
  title,
  hint,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h2 id={id} className="text-lg font-semibold tracking-tight">
          {title}
        </h2>
        {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}
